import React, { useEffect, useRef, useState } from 'react';
import { Users, MessageCircle, Smile, Award } from 'lucide-react';
import { impactStats } from '../mock';

const iconMap = [Users, MessageCircle, Smile, Award];
const toneMap = {
  aqua: { bg: '#79c4c7', soft: '#F0F8F8' },
  purple: { bg: '#6e12ed', soft: '#F3EBFE' },
};

const Counter = ({ end, suffix, duration = 1800 }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const step = (t) => {
            const p = Math.min((t - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.floor(eased * end));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{n.toLocaleString()}{suffix}</span>;
};

const Impact = () => {
  return (
    <section id="impact" className="relative py-24 lg:py-28 bg-white overflow-hidden">
      <div className="brush-swash w-64 h-14 top-16 -left-10" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-neutral-500 mb-4">Our Impact</div>
            <h2 className="font-serif text-4xl lg:text-6xl font-bold leading-[1.02] text-[#0E1112]">
              Thanks to you,<br />
              our impact <span className="hl-box hl-cyan">grows</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-neutral-700 text-lg leading-relaxed">
              For over 38 years, ACCI has kept its doors open, its outreach warm and its counselling
              rooted in community. Here’s what your stand makes possible each year.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {impactStats.map((s, i) => {
            const Icon = iconMap[i];
            const t = toneMap[s.tone];
            return (
              <div
                key={s.label}
                className="relative bg-white rounded-2xl p-7 hover:-translate-y-1 transition-transform duration-300 border border-black/10 shadow-sm hover:shadow-md"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                  style={{ backgroundColor: t.bg }}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div
                  className="font-serif text-5xl lg:text-6xl font-bold leading-none"
                  style={{ color: t.bg }}
                >
                  <Counter end={s.number} suffix={s.suffix} />
                </div>
                <div className="mt-4 text-neutral-700 leading-snug text-sm">{s.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Impact;
